import { useEffect } from "react";
import { Printer, CheckCircle2, AlertTriangle, Info } from "lucide-react";
import { useNoIndex } from "../lib/useNoIndex";

/**
 * LaunchChecklist — printable, standalone page for the ops-day DNS
 * flip. Uses print-optimized CSS so opening browser Print gives a
 * clean, ink-friendly hard copy without any of the marketing chrome.
 *
 * URL: /launch-checklist (noindex, not linked from anywhere — internal
 * ops-only). Content mirrors /app/memory/LAUNCH_CHECKLIST.md but
 * hand-rendered here so it prints as a real document.
 */
export default function LaunchChecklist() {
  useNoIndex();

  // Set a print-friendly document title so the printer header reads clean
  useEffect(() => {
    const prev = document.title;
    document.title = "My Rich Aunt — Launch Checklist";
    return () => { document.title = prev; };
  }, []);

  const doPrint = () => {
    if (typeof window !== "undefined") window.print();
  };

  return (
    <>
      <PrintStyles />
      <main data-testid="launch-checklist-page" className="min-h-screen bg-sand print:bg-white">
        {/* Top bar — hidden on print */}
        <div className="no-print sticky top-0 z-10 border-b border-border/60 bg-sand/90 backdrop-blur-sm">
          <div className="mx-auto flex max-w-4xl items-center justify-between px-6 py-4 lg:px-10">
            <div>
              <p className="text-[13px] font-semibold uppercase tracking-[0.22em] text-tangerine">
                Internal · ops only
              </p>
              <p className="font-serif text-3xl text-ink">Launch Checklist</p>
            </div>
            <button
              type="button"
              onClick={doPrint}
              data-testid="launch-checklist-print"
              className="inline-flex items-center gap-1.5 rounded-full bg-ink px-5 py-2.5 text-[13px] font-semibold text-sand transition-colors hover:bg-forest"
            >
              <Printer className="h-3.5 w-3.5" strokeWidth={2} /> Print
            </button>
          </div>
        </div>

        {/* Document */}
        <article className="mx-auto max-w-4xl px-6 py-10 print:max-w-none print:px-0 print:py-0 lg:px-10">
          <header className="border-b-2 border-ink pb-6 print:pb-3">
            <div className="flex items-baseline justify-between gap-4">
              <div>
                <p className="text-[13px] font-semibold uppercase tracking-[0.22em] text-olive">
                  My Rich Aunt
                </p>
                <h1 className="mt-1 font-serif text-3xl leading-[1.05] tracking-tight text-ink print:">
                  DNS Flip · Launch Checklist
                </h1>
              </div>
              <p className="hidden text-right text-[13px] font-semibold uppercase tracking-[0.18em] text-olive print:block">
                Printed
                <br />
                <span className="text-ink">{new Date().toLocaleDateString(undefined, {
                  year: "numeric", month: "short", day: "numeric",
                })}</span>
              </p>
            </div>
            <p className="mt-3 max-w-2xl text-base leading-relaxed text-olive">
              Prioritized by time-sensitivity. Green = must-do. Yellow = should-do. Blue = nice-to-have. Skip nothing in green.
            </p>
          </header>

          {/* T-0 */}
          <Section
            timing="T · 0"
            title="The moment you flip DNS"
            subtitle="Should take ~5 minutes"
          >
            <Item level="must">
              <b>Point DNS at Emergent.</b> A / CNAME record for <Code>myrichaunt.org</Code> (and <Code>www.myrichaunt.org</Code>) points to your Emergent host. HTTPS provisions automatically.
            </Item>
            <Item level="must">
              <b>Cloudflare SSL, if used, must be Full (Strict).</b> Flexible breaks auth cookies and API calls.
            </Item>
            <Item level="must">
              Load <Code>https://myrichaunt.org/api/</Code> in incognito — expect <Code>&#123;"message":"Hello World"&#125;</Code>. Padlock is green.
            </Item>
            <Item level="note">
              If you get a cert warning, wait 5 minutes for the cert to provision. Still broken after 15? Contact Emergent support.
            </Item>
          </Section>

          {/* T+15 */}
          <Section
            timing="T + 15 min"
            title="Update production env vars"
            subtitle="Set once, then a redeploy picks it up"
          >
            <Item level="must">
              In your Emergent backend env, set <Code>FRONTEND_ORIGIN=https://myrichaunt.org</Code>. Used to build the shareable link inside admin approval emails.
            </Item>
            <Item level="must">
              Confirm <Code>REACT_APP_BACKEND_URL</Code> reflects the new domain. React bakes env vars at build time — you may need a redeploy so the new URL is in the compiled JS.
              <p className="mt-1 text-[13px] text-olive">Test: DevTools Network tab, click Donate — the API request should go to <Code>myrichaunt.org/api/beta-signups</Code>, not the preview host.</p>
            </Item>
            <Item level="should">
              <b>Google OAuth (donor sign-in):</b> Emergent-managed. If donor Google sign-in fails after the flip, message Emergent to add <Code>myrichaunt.org</Code> to their OAuth allowlist. Usually same-day.
            </Item>
          </Section>

          {/* T+30 */}
          <Section
            timing="T + 30 min"
            title="Smoke test on the real domain"
            subtitle="If any one of these fails, roll back DNS immediately"
          >
            <Group title="Marketing surfaces">
              <Check>Load <Code>/</Code> — landing renders, Donate button in Navbar opens Coming Soon dialog</Check>
              <Check><Code>/team</Code> loads</Check>
              <Check><Code>/privacy</Code> loads</Check>
              <Check><Code>/terms</Code> loads</Check>
              <Check><Code>/robots.txt</Code> loads (contains <Code>Disallow: /demo</Code>, <Code>/admin</Code>, etc.)</Check>
              <Check><Code>/sitemap.xml</Code> loads (4 marketing URLs)</Check>
            </Group>

            <Group title="Prototype / demo">
              <Check><Code>/demo</Code> — Landing (mocked) loads</Check>
              <Check><Code>/demo/campaign/2</Code> — Andre's campaign renders</Check>
              <Check>View source on <Code>/demo</Code> — includes <Code>&lt;meta name="robots" content="noindex, nofollow"&gt;</Code></Check>
            </Group>

            <Group title="Live app">
              <Check><Code>/campaign/cmp_ff6d0442a13d</Code> — Kyoto campaign renders publicly</Check>
              <Check>Cover image + supporter wall load</Check>
              <Check>Donate button → Coming Soon dialog</Check>
              <Check>Share bar renders under Donate — X, WhatsApp, Instagram, Copy Link</Check>
            </Group>

            <Group title="Student gate">
              <Check><Code>/register</Code> redirects to <Code>/students-coming-soon</Code> (frontend gate)</Check>
              <Check><Code>POST /api/auth/register</Code> returns HTTP 503 (backend gate)</Check>
              <Check>Signing up on <Code>/students-coming-soon</Code> with a fresh email creates a <Code>beta_signups</Code> row</Check>
            </Group>

            <Group title="Existing student login still works">
              <Check>Log in as Maya (<Code>maya.student@example.edu</Code>) via <Code>/login</Code></Check>
              <Check>Dashboard renders with Approval banner (Kyoto campaign live)</Check>
              <Check>Only one Sign out button visible in the Navbar</Check>
            </Group>

            <Group title="Admin console">
              <Check>Log in as <Code>admin@myrichaunt.org</Code></Check>
              <Check><Code>/admin</Code> loads with 5 tabs</Check>
              <Check>Audit Log tab shows past actions</Check>
              <Check>Bulk-action bar appears when you select a row</Check>
            </Group>

            <Group title="Email deliverability" tone="should">
              <Check>Submit a real email in the Beta Signup on <Code>/</Code>. Check the inbox — arrives from Emergent-managed Resend proxy</Check>
              <Check>FROM name reads "My Rich Aunt". No SPF/DKIM/DMARC records needed on myrichaunt.org — Emergent owns the sender domain</Check>
            </Group>

            <Group title="Social preview" tone="should">
              <Check>Paste <Code>https://myrichaunt.org</Code> into X, LinkedIn, and iMessage. Preview card renders.</Check>
              <Check>If OG image 404s, generate <Code>og-image.jpg</Code> and drop it in <Code>frontend/public/</Code></Check>
              <Check>Purge caches at <Code>cards-dev.twitter.com/validator</Code> and LinkedIn Post Inspector</Check>
            </Group>
          </Section>

          {/* T+24h */}
          <Section
            timing="T + 24 h"
            title="SEO + observability"
          >
            <Item level="must">
              <b>Google Search Console:</b> verify domain at <Code>search.google.com/search-console</Code>, submit sitemap, request indexing on <Code>/</Code>, <Code>/team</Code>, <Code>/privacy</Code>, <Code>/terms</Code>.
            </Item>
            <Item level="should">
              <b>Bing Webmaster Tools:</b> mirror the sitemap submission at <Code>bing.com/webmasters</Code>.
            </Item>
            <Item level="should">
              <b>Confirm no leaked preview URLs</b> — run <Code>grep -rn "preview.emergentagent" /app/frontend/build</Code> in the Emergent shell. Zero matches after a fresh build.
            </Item>
            <Item level="nice">
              <b>Analytics:</b> add a lightweight script (Plausible / Fathom / GA4) to <Code>index.html</Code> if you want traffic data.
            </Item>
          </Section>

          {/* T+1w */}
          <Section
            timing="T + 1 wk"
            title="Turn things on"
          >
            <Item level="must">
              <b>Seed the real student cohort.</b> Once 3–5 students are manually verified out of band:
              <ol className="ml-5 mt-2 list-decimal space-y-1 text-[13px] text-olive">
                <li>Flip <Code>STUDENT_CAMPAIGNS_OPEN=true</Code> in both <Code>frontend/.env</Code> and <Code>backend/.env</Code> in the Emergent console</li>
                <li>Redeploy</li>
                <li>Have each student use <Code>/register</Code> — normal flow</li>
                <li>Verify each via the admin console → Student Verification tab</li>
                <li>Approve their drafts → they go live</li>
              </ol>
            </Item>
            <Item level="must">
              <b>Turn on real Stripe.</b> Ask your engineer to swap <Code>DonateComingSoonDialog</Code> → real Stripe Checkout across three surfaces (Home, LiveCampaign, Team/Privacy/Terms). Run a $1 test donation end-to-end. Refund it.
            </Item>
            <Item level="must">
              <b>Send the promised follow-up email</b> to every row in <Code>beta_signups</Code>. Segment by source (donate_coming_soon vs student_coming_soon). Broken pledge = broken brand trust. Do not skip.
            </Item>
          </Section>

          {/* Ongoing */}
          <Section
            timing="Weekly"
            title="Ongoing operations"
          >
            <Item level="must">Review admin Audit Log — spot anomalies</Item>
            <Item level="must">Clear the flagged wall messages queue</Item>
            <Item level="must">Approve or return student drafts</Item>
            <Item level="must">Authorize Friday's disbursement batch (once Stripe is live)</Item>
            <Item level="should">Send progress updates to the "Family" list (matches the copy on the trust card)</Item>
          </Section>

          {/* Rollback */}
          <section className="mt-10 rounded-2xl border-2 border-scarlet bg-scarlet/[0.04] p-6 print:mt-6 print:break-inside-avoid">
            <div className="flex items-start gap-3">
              <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-scarlet text-sand">
                <AlertTriangle className="h-4 w-4" strokeWidth={2.4} />
              </span>
              <div className="min-w-0 flex-1">
                <h3 className="font-serif text-3xl leading-tight text-ink print:">Emergency rollback</h3>
                <p className="mt-2 text-base leading-relaxed text-olive">
                  If <b>any</b> smoke test fails and you can't fix it in 15 minutes:
                </p>
                <ol className="ml-5 mt-2 list-decimal space-y-1 text-[13px] text-ink">
                  <li>Revert the <Code>myrichaunt.org</Code> A/CNAME to whatever it pointed at before (or delete it)</li>
                  <li>Message your engineer — they'll debug from the Emergent side</li>
                </ol>
                <p className="mt-3 rounded-md border border-scarlet/20 bg-bone px-3 py-2 text-[13px] leading-relaxed text-ink">
                  <b>TTL matters.</b> If your DNS TTL is 3600s, rollback also takes up to 1 hour to propagate. <b>Lower DNS TTL to 300s (5 min) 24 hours BEFORE you flip</b>, so rollbacks are fast.
                </p>
              </div>
            </div>
          </section>

          {/* Signature block for print */}
          <footer className="mt-16 hidden border-t border-ink/20 pt-4 text-[13px] uppercase tracking-[0.18em] text-olive print:block">
            My Rich Aunt · Internal ops document · Do not distribute externally
          </footer>
        </article>
      </main>
    </>
  );
}

// ─── Layout primitives ─────────────────────────────────────────────
const Section = ({ timing, title, subtitle, children }) => (
  <section className="mt-10 print:mt-6 print:break-inside-avoid">
    <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1 border-b border-border pb-3">
      <div>
        <p className="text-[13px] font-semibold uppercase tracking-[0.22em] text-tangerine">
          {timing}
        </p>
        <h2 className="mt-1 font-serif text-3xl leading-tight text-ink print:">{title}</h2>
      </div>
      {subtitle && (
        <p className="text-[13px] italic text-olive">{subtitle}</p>
      )}
    </div>
    <ul className="mt-5 space-y-3 print:mt-3 print:space-y-2">{children}</ul>
  </section>
);

const Item = ({ level = "should", children }) => {
  const meta = LEVEL_META[level];
  return (
    <li className="flex items-start gap-3">
      <span
        className={`mt-1 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full ${meta.dotCls}`}
        aria-hidden
      >
        <meta.Icon className="h-2.5 w-2.5" strokeWidth={2.5} />
      </span>
      <div className="min-w-0 flex-1 text-base leading-relaxed text-ink print:">
        {children}
      </div>
    </li>
  );
};

const Check = ({ children }) => (
  <li className="flex items-start gap-3">
    <span
      aria-hidden
      className="mt-[3px] inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border-2 border-ink print:h-3 print:w-3"
    />
    <span className="min-w-0 flex-1 text-[13px] leading-relaxed text-ink print:">
      {children}
    </span>
  </li>
);

const Group = ({ title, tone, children }) => (
  <li className={`rounded-lg border p-4 print:p-3 print:break-inside-avoid ${
    tone === "should" ? "border-tangerine/25 bg-tangerine/[0.04]" : "border-border bg-bone"
  }`}>
    <p className={`text-[13px] font-semibold uppercase tracking-[0.22em] ${ tone === "should" ? "text-tangerine" : "text-olive" }`}>{title}</p>
    <ul className="mt-2 space-y-1.5 print:space-y-1">{children}</ul>
  </li>
);

const Code = ({ children }) => (
  <code className="rounded bg-ink/6 px-1.5 py-0.5 text-[0.85em] font-mono text-ink">{children}</code>
);

const LEVEL_META = {
  must:   { dotCls: "bg-grass text-sand",     Icon: CheckCircle2 },
  should: { dotCls: "bg-tangerine text-sand", Icon: AlertTriangle },
  nice:   { dotCls: "bg-cobalt text-sand",    Icon: Info },
  note:   { dotCls: "bg-olive text-sand",     Icon: Info },
};


// Print-optimized CSS injected once per mount. Kept inline so the page
// prints beautifully anywhere without touching the global stylesheet.
const PrintStyles = () => (
  <style>{`
    @media print {
      .no-print { display: none !important; }
      body { background: #fff !important; }
      * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
      a { color: #000 !important; text-decoration: none !important; }
      @page { size: Letter; margin: 0.55in 0.6in; }
    }
  `}</style>
);
